
import { Injectable } from '@angular/core';
import { Customer } from '../models/index';
import { MOCK_CUSTOMERS } from '../data/mock-data';
import { getSupabase, isSupabaseConfigured } from '../firebase';
import { SupabaseClient } from '@supabase/supabase-js';

const CUSTOMER_TABLE = 'customers';

@Injectable({ providedIn: 'root' })
export class CustomerService {
  private supabase: SupabaseClient | null = getSupabase();

  private toCustomerDB(customer: Partial<Omit<Customer, 'hn'>>): any {
    return {
      id: customer.id,
      store_id: customer.storeId,
      citizen_id: customer.citizenId,
      full_name: customer.fullName,
      birth_date: customer.birthDate,
      phone: customer.phone,
      address: customer.address,
      id_card_url: customer.idCardUrl,
      profile_image_url: customer.profile_image_url,
      points_balance: customer.pointsBalance,
      tier_level: customer.tierLevel,
      card_uid: customer.card_uid,
      medical_history: customer.medicalHistory,
      allergies: customer.allergies,
      underlying_disease: customer.underlyingDisease,
      blood_type: customer.bloodType,
      temperature_c: customer.temperature_c,
      pulse_rate: customer.pulse_rate,
      respiration_rate: customer.respiration_rate,
      blood_pressure: customer.blood_pressure,
      weight_kg: customer.weight_kg,
      height_cm: customer.height_cm,
    };
  }
  
  private fromCustomerDB(c: any): Customer {
    if (!c) return null as any;
    return {
      id: c.id,
      storeId: c.store_id,
      hn: c.hn,
      fullName: c.full_name,
      citizenId: c.citizen_id,
      birthDate: c.birth_date,
      phone: c.phone,
      address: c.address,
      idCardUrl: c.id_card_url,
      profile_image_url: c.profile_image_url,
      pointsBalance: c.points_balance,
      tierLevel: c.tier_level,
      medicalHistory: c.medical_history,
      allergies: c.allergies,
      underlyingDisease: c.underlying_disease,
      bloodType: c.blood_type,
      card_uid: c.card_uid,
      // FIX: Map correctly from "createdAt" (DB column) or fallback to created_at
      createdAt: c.createdAt || c.created_at, 
      temperature_c: c.temperature_c,
      pulse_rate: c.pulse_rate,
      respiration_rate: c.respiration_rate,
      blood_pressure: c.blood_pressure,
      weight_kg: c.weight_kg,
      height_cm: c.height_cm,
    } as Customer;
  }

  async getCustomers(): Promise<Customer[]> {
    if (!isSupabaseConfigured() || !this.supabase) {
      return Promise.resolve(MOCK_CUSTOMERS);
    }
    const { data, error } = await this.supabase.from(CUSTOMER_TABLE).select('*');
    if (error) throw error;
    return (data || []).map(c => this.fromCustomerDB(c));
  }

  async getCustomerByCardUid(cardUid: string): Promise<Customer | null> {
    if (!isSupabaseConfigured() || !this.supabase) return Promise.resolve(null);
    const { data, error } = await this.supabase.rpc('get_customer_by_card_uid', { p_card_uid: cardUid });
    if (error) throw error;
    if (!data) return null;
    return this.fromCustomerDB(data);
  }

  async addCustomer(customerData: Omit<Customer, 'id' | 'createdAt' | 'hn'>): Promise<Customer> {
    if (!isSupabaseConfigured() || !this.supabase) {
      const newCustomer: Customer = { ...customerData, id: `mock-${Date.now()}`, hn: `MOCK${Date.now()}`, createdAt: new Date().toISOString() };
      return Promise.resolve(newCustomer);
    }
    
    // Ensure undefined values are sent as null to match RPC signature
    const { data, error } = await this.supabase.rpc('create_customer_with_hn', {
        p_full_name: customerData.fullName,
        p_citizen_id: customerData.citizenId || null,
        p_birth_date: customerData.birthDate || null,
        p_phone: customerData.phone || null,
        p_address: customerData.address || null,
        p_medical_history: customerData.medicalHistory || null,
        p_allergies: customerData.allergies || null,
        p_underlying_disease: customerData.underlyingDisease || null,
        p_blood_type: customerData.bloodType || null,
        p_card_uid: customerData.card_uid || null,
        p_profile_image_url: customerData.profile_image_url || null,
        p_id_card_url: customerData.idCardUrl || null
    }).single();
    
    if (error) throw error;
    return this.fromCustomerDB(data);
  }

  async updateCustomer(updatedCustomer: Customer): Promise<Customer> {
    if (!isSupabaseConfigured() || !this.supabase) return Promise.resolve(updatedCustomer);
    const { id, ...customerData } = updatedCustomer;
    const dbPayload = this.toCustomerDB(customerData);
    const { data, error } = await this.supabase.from(CUSTOMER_TABLE).update(dbPayload).eq('id', id).select().single();
    if (error) throw error;
    return this.fromCustomerDB(data);
  }

  async deleteCustomer(customerId: string): Promise<void> {
    if (!isSupabaseConfigured() || !this.supabase) return Promise.resolve();
    const { error } = await this.supabase.from(CUSTOMER_TABLE).delete().eq('id', customerId);
    if (error) throw error;
  }

  async batchAddCustomers(customers: Omit<Customer, 'id' | 'createdAt'>[]): Promise<{ success: boolean; message: string }> {
    if (!isSupabaseConfigured() || !this.supabase) return { success: false, message: 'Database not connected.' };
    try {
      const { error: deleteError } = await this.supabase.from(CUSTOMER_TABLE).delete().neq('id', '00000000-0000-0000-0000-000000000000');
      if (deleteError) throw deleteError;
      const customersToInsert = customers.map(c => this.toCustomerDB(c));
      const { error: insertError } = await this.supabase.from(CUSTOMER_TABLE).insert(customersToInsert);
      if (insertError) throw insertError;
      return { success: true, message: `${customers.length} customers imported successfully.` };
    } catch (e) {
      return { success: false, message: e instanceof Error ? e.message : 'Unknown error' };
    }
  }

  async exportCustomers(startDate?: string, endDate?: string): Promise<any[]> {
    if (!isSupabaseConfigured() || !this.supabase) {
        console.warn('Mock Export: Returning empty list');
        return []; 
    }
    const { data, error } = await this.supabase.rpc('export_customer_data', {
        p_start_date: startDate || null,
        p_end_date: endDate || null
    });
    if (error) throw new Error(error.message);
    return data || [];
  }

  // Improved Base64 to Blob helper
  private base64ToBlob(dataURI: string): { blob: Blob, ext: string } {
    let base64 = dataURI;
    let mimeType = 'image/jpeg'; 
    let ext = 'jpg';

    // 1. Clean up Data URI prefix
    if (dataURI.includes('base64,')) {
        const parts = dataURI.split(',');
        base64 = parts[1]; 
        
        const matches = parts[0].match(/:(.*?);/);
        if (matches && matches.length > 1) {
            mimeType = matches[1];
            if (mimeType === 'image/png') ext = 'png';
            if (mimeType === 'image/webp') ext = 'webp';
        }
    }

    // 2. Remove whitespace
    base64 = base64.replace(/\s/g, '');

    // 3. Decode
    try {
        const byteString = atob(base64);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        
        return { blob: new Blob([ab], { type: mimeType }), ext };
    } catch (e) {
        console.error("Failed to decode base64 string", e);
        throw new Error("Invalid image data format. Please try taking the photo again.");
    }
  }

  // --- IMAGE WATERMARKING LOGIC ---
  async processIdCardImage(imageBase64WithPrefix: string, signatureBase64: string | null, name: string, watermarkText: string): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const MAX_WIDTH = 1024;
            let width = img.width;
            let height = img.height;
            
            if (width > MAX_WIDTH) {
                height *= MAX_WIDTH / width;
                width = MAX_WIDTH;
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) { reject('Canvas error'); return; }

            ctx.drawImage(img, 0, 0, width, height);
            ctx.save();
            ctx.translate(width / 2, height / 2);
            ctx.rotate(-25 * Math.PI / 180);
            
            const fontSize = Math.floor(width * 0.035);
            ctx.font = `bold ${fontSize}px Sarabun, sans-serif`;
            ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
            ctx.textAlign = 'center';
            
            const text1 = watermarkText || "ใช้สำหรับขอใบสั่งจ่ายสมุนไพรควบคุม (กัญชา) เท่านั้น";
            const text2 = "--------------------------------------------------------";
            
            ctx.fillText(text2, 0, -20);
            ctx.fillText(text1, 0, 10);
            ctx.fillText(text2, 0, 40);

            ctx.restore();

            const footerX = width * 0.65;
            const footerY = height * 0.75;
            
            ctx.font = `bold ${Math.floor(width * 0.03)}px Sarabun, sans-serif`;
            ctx.fillStyle = 'rgba(0, 0, 139, 1)';
            ctx.textAlign = 'left';

            ctx.fillText("สำเนาถูกต้อง", footerX, footerY);

            if (signatureBase64) {
                const sigImg = new Image();
                sigImg.onload = () => {
                    const sigWidth = width * 0.25;
                    const sigHeight = sigWidth * (sigImg.height / sigImg.width);
                    ctx.drawImage(sigImg, footerX, footerY + 10, sigWidth, sigHeight);
                    ctx.fillText(`( ${name} )`, footerX, footerY + sigHeight + 25);
                    resolve(canvas.toDataURL('image/jpeg', 0.85));
                };
                sigImg.onerror = () => {
                     ctx.fillText(`( ${name} )`, footerX, footerY + 50);
                     resolve(canvas.toDataURL('image/jpeg', 0.85));
                }
                sigImg.src = signatureBase64;
            } else {
                ctx.fillText(`( ${name} )`, footerX, footerY + 50);
                resolve(canvas.toDataURL('image/jpeg', 0.85));
            }
        };
        img.onerror = reject;
        img.src = imageBase64WithPrefix;
    });
  }

  async uploadCustomerPhoto(customerId: string, photoData: string): Promise<string> {
    return this._performUpload(customerId, photoData, 'customer-photos', 'profile_image_url');
  }

  async uploadIdCardPhoto(customerId: string, photoData: string): Promise<string> {
    return this._performUpload(customerId, photoData, 'id-card-photos', 'id_card_url');
  }

  // Unified upload logic to reduce code duplication
  private async _performUpload(customerId: string, photoData: string, bucketName: string, dbColumn: string): Promise<string> {
    if (!isSupabaseConfigured() || !this.supabase) {
      console.warn("Mock mode: Upload skipped.");
      return "https://picsum.photos/200";
    }

    try {
        const { blob, ext } = this.base64ToBlob(photoData);
        
        const { data: customer, error: fetchError } = await this.supabase
            .from('customers')
            .select('store_id')
            .eq('id', customerId)
            .single();

        if(fetchError || !customer) {
            console.error("Fetch customer error:", fetchError);
            throw new Error("Customer not found or access denied.");
        }

        const fileType = bucketName === 'id-card-photos' ? 'idcard' : 'profile';
        const filePath = `${customer.store_id}/${customerId}_${fileType}.${ext}`;

        const { error: uploadError } = await this.supabase.storage
            .from(bucketName)
            .upload(filePath, blob, { upsert: true, contentType: blob.type });

        if (uploadError) {
            console.error("[Upload] Storage Error:", uploadError);
            throw new Error(`Upload Failed: ${uploadError.message}`);
        }

        const { data: publicUrlData } = this.supabase.storage
            .from(bucketName)
            .getPublicUrl(filePath);

        const publicUrl = `${publicUrlData.publicUrl}?t=${Date.now()}`;

        const { error: updateError } = await this.supabase
            .from('customers')
            .update({ [dbColumn]: publicUrl })
            .eq('id', customerId);

        if (updateError) {
             console.error("[Upload] DB Update Error:", updateError);
             throw new Error(`Database Update Failed: ${updateError.message}`);
        }
        
        return publicUrl;

    } catch (e: any) {
        console.error("Upload Critical Error:", e);
        throw new Error(e.message || 'Unknown error uploading photo');
    }
  }
}
